import {
  classifyTransactionErrorFrom,
  type TransactionErrorCategory
} from '@/lib/observability/client/trackers/transaction/error-classifier'
import type { WalletType } from '@/lib/observability/shared/wallet-type'

import type {
  AnalyticsEventMap,
  CheckoutEntryPoint,
  CheckoutFlowVersion,
  CheckoutStepOutcome,
  CheckoutStepType,
  TxFlowType
} from '../events'
import { trackAnalyticsEvent } from '../track'

type PositionType = AnalyticsEventMap['position_manager_action_selected']['position_type']

interface PositionManagerActionParams {
  action: string
  positionType: PositionType
  chainId: number
}

export function trackPositionManagerActionSelected(params: PositionManagerActionParams) {
  trackAnalyticsEvent('position_manager_action_selected', {
    action: params.action,
    position_type: params.positionType,
    chain_id: params.chainId
  })
}

export interface ReviewDialogParams {
  reviewSessionId: string
  flowVersion: CheckoutFlowVersion
  operationTypes: string[]
  entryPoint: CheckoutEntryPoint
  txFlowType: TxFlowType
  totalSteps?: number
}

export interface CheckoutAttemptTrackingContext extends ReviewDialogParams {
  attemptId: string
  totalSteps: number
}

export interface CheckoutAttemptTrackingInput extends ReviewDialogParams {
  attemptId: string
}

/**
 * Derives the funnel dimensions every V2 review dialog reports the same way:
 * Safe wallets batch everything into one step (`simple`); otherwise the requirement
 * list decides `simple` (none) / `signature_required` (any off-chain signature) /
 * `bundled` (on-chain approvals only), and `totalSteps` counts requirements + the
 * final tx. Keeps the derivation in one place so new flows can't drift.
 */
export function buildReviewDialogParams({
  reviewSessionId,
  entryPoint,
  operationTypes,
  isSafe,
  requirements,
  batchesOnchainRequirements = false
}: {
  reviewSessionId: string
  entryPoint: CheckoutEntryPoint
  operationTypes: string[]
  isSafe: boolean
  /** The dialog's `getRequirements()` output; anything with a `sign` function is a signature. */
  requirements: ReadonlyArray<unknown> | undefined
  /** On-chain requirements are batched into the final transaction; signatures stay separate steps. */
  batchesOnchainRequirements?: boolean
}): ReviewDialogParams {
  // Structural twin of morpho-sdk's `isRequirementSignature`, kept import-free here.
  const signatureRequirementCount =
    requirements?.filter(
      requirement =>
        typeof requirement === 'object' &&
        requirement !== null &&
        'sign' in requirement &&
        typeof (requirement as { sign: unknown }).sign === 'function'
    ).length ?? 0
  const hasSignatureRequirement = signatureRequirementCount > 0
  const counted = batchesOnchainRequirements
    ? signatureRequirementCount
    : (requirements?.length ?? 0)

  return {
    reviewSessionId,
    flowVersion: 'v2',
    operationTypes,
    entryPoint,
    txFlowType: getTxFlowType({
      isSafe,
      hasSignatureRequirement,
      batchesOnchainRequirements,
      hasRequirements: requirements !== undefined && requirements.length > 0
    }),
    totalSteps: isSafe ? 1 : counted + 1
  }
}

function getTxFlowType({
  isSafe,
  hasSignatureRequirement,
  batchesOnchainRequirements,
  hasRequirements
}: {
  isSafe: boolean
  hasSignatureRequirement: boolean
  batchesOnchainRequirements: boolean
  hasRequirements: boolean
}): ReviewDialogParams['txFlowType'] {
  if (isSafe) return 'simple'
  if (hasSignatureRequirement) return 'signature_required'
  if (batchesOnchainRequirements || !hasRequirements) return 'simple'
  return 'bundled'
}

type ReviewDialogEventProperties = Omit<
  AnalyticsEventMap['review_dialog_opened'],
  'total_steps'
> & { total_steps?: number }

function getReviewDialogEventProperties(params: ReviewDialogParams): ReviewDialogEventProperties {
  return {
    review_session_id: params.reviewSessionId,
    flow_version: params.flowVersion,
    operation_types: params.operationTypes,
    entry_point: params.entryPoint,
    tx_flow_type: params.txFlowType,
    ...(params.totalSteps !== undefined ? { total_steps: params.totalSteps } : {})
  }
}

export function trackReviewDialogOpened(params: ReviewDialogParams) {
  trackAnalyticsEvent('review_dialog_opened', getReviewDialogEventProperties(params))
}

export function trackReviewDialogClosed(params: ReviewDialogParams) {
  trackAnalyticsEvent('review_dialog_closed', {
    ...getReviewDialogEventProperties(params),
    close_trigger: 'close_button'
  })
}

export function trackReviewConfirmed(params: CheckoutAttemptTrackingInput) {
  trackAnalyticsEvent('review_confirmed', {
    ...getReviewDialogEventProperties(params),
    attempt_id: params.attemptId
  })
}

export function getCheckoutTrackingProperties(tracking: CheckoutAttemptTrackingContext) {
  return {
    review_session_id: tracking.reviewSessionId,
    attempt_id: tracking.attemptId,
    flow_version: tracking.flowVersion,
    operation_types: tracking.operationTypes,
    entry_point: tracking.entryPoint,
    tx_flow_type: tracking.txFlowType
  }
}

interface TransactionFlowCancelledParams {
  tracking: CheckoutAttemptTrackingContext
  chainId: number
  walletType: WalletType
  step: string
}

export function trackTransactionFlowCancelled(params: TransactionFlowCancelledParams) {
  trackAnalyticsEvent('transaction_flow_cancelled', {
    ...getCheckoutTrackingProperties(params.tracking),
    chain_id: params.chainId,
    wallet_type: params.walletType,
    step: params.step
  })
}

interface TransactionStepResolvedParams {
  tracking: CheckoutAttemptTrackingContext
  chainId: number
  walletType: WalletType
  stepType: CheckoutStepType
  stepIndex: number
  outcome: CheckoutStepOutcome
  errorCategory?: TransactionErrorCategory
}

export function trackTransactionStepResolved(params: TransactionStepResolvedParams) {
  trackAnalyticsEvent('transaction_step_resolved', {
    ...getCheckoutTrackingProperties(params.tracking),
    chain_id: params.chainId,
    wallet_type: params.walletType,
    step_type: params.stepType,
    step_index: params.stepIndex,
    total_steps: params.tracking.totalSteps,
    outcome: params.outcome,
    ...(params.errorCategory ? { error_category: params.errorCategory } : {})
  })
}

/**
 * Tracks a step resolution only when checkout tracking is active.
 * Reduces boilerplate at call sites by handling the null check internally.
 */
export function maybeTrackStepResolved(
  tracking: CheckoutAttemptTrackingContext | undefined,
  params: Omit<TransactionStepResolvedParams, 'tracking'>
) {
  if (!tracking) return
  trackTransactionStepResolved({ ...params, tracking })
}

/**
 * Handles both step-level failure tracking and user-rejection tracking.
 * Consolidates the duplicated trackCheckoutStepFailure pattern from transaction contexts.
 */
export function trackCheckoutStepFailure(params: {
  tracking: CheckoutAttemptTrackingContext | undefined
  error: unknown
  chainId: number
  chainName: string
  walletType: WalletType
  stepType: CheckoutStepType
  stepIndex: number
  rejectionStep: string
  operationTypes: string[]
}) {
  const errorCategory = classifyTransactionErrorFrom(params.error)

  if (params.tracking) {
    trackTransactionStepResolved({
      tracking: params.tracking,
      chainId: params.chainId,
      walletType: params.walletType,
      stepType: params.stepType,
      stepIndex: params.stepIndex,
      outcome: errorCategory === 'user_rejected' ? 'rejected' : 'failed',
      errorCategory
    })
  }

  maybeTrackUserRejection(errorCategory, {
    chainId: params.chainId,
    chainName: params.chainName,
    operationTypes: params.tracking?.operationTypes ?? params.operationTypes,
    walletType: params.walletType,
    step: params.rejectionStep,
    checkoutTracking: params.tracking
  })
}

interface TransactionUserRejectedParams {
  chainId: number
  chainName: string
  operationTypes: string[]
  walletType: WalletType
  step: string
  checkoutTracking?: CheckoutAttemptTrackingContext
}

export function trackTransactionUserRejected(params: TransactionUserRejectedParams) {
  trackAnalyticsEvent('transaction_user_rejected', {
    chain_id: params.chainId,
    chain_name: params.chainName,
    operation_types: params.operationTypes,
    wallet_type: params.walletType,
    step: params.step,
    ...(params.checkoutTracking ? getCheckoutTrackingProperties(params.checkoutTracking) : {})
  })
}

/**
 * Checks if an error is a user rejection and tracks it if so.
 * Consolidates the repeated classify-and-track pattern from transaction contexts.
 */
export function maybeTrackUserRejection(
  errorCategory: TransactionErrorCategory,
  params: TransactionUserRejectedParams
) {
  if (errorCategory === 'user_rejected') {
    trackTransactionUserRejected(params)
  }
}
